import type { NostrEvent } from '@nostrify/nostrify';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { FlagSVG } from './FlagSVG';
import { hexToBytes } from '@/lib/flagUtils';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface FlagCardProps {
  event: NostrEvent;
  onClick: (event: NostrEvent) => void;
  isOwned?: boolean;
}

export function FlagCard({ event, onClick, isOwned }: FlagCardProps) {
  const title = event.tags.find(([n]) => n === 'title')?.[1] ?? 'Untitled';
  const dataHex = event.tags.find(([n]) => n === 'data')?.[1] ?? '';
  const encoding = event.tags.find(([n]) => n === 'encoding')?.[1] ?? 'hex';
  const author = useAuthor(event.pubkey);
  const displayName = author.data?.metadata?.name ?? event.pubkey.slice(0, 8) + '…';
  const avatar = author.data?.metadata?.picture;
  const npub = nip19.npubEncode(event.pubkey);

  let bytes: number[] = [];
  try { bytes = hexToBytes(dataHex); } catch { /* ignore */ }

  return (
    <div className="group rounded-xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Flag image — opens detail */}
      <div className="relative cursor-pointer" onClick={() => onClick(event)}>
        <FlagSVG bytes={bytes} className="w-full" width={400} height={250} />
        {isOwned && (
          <Badge className="absolute top-2 right-2 bg-primary/90 text-primary-foreground text-xs">
            Your flag
          </Badge>
        )}
      </div>

      {/* Footer — title clicks detail, author clicks profile */}
      <div className="p-3 flex items-center gap-2">
        <Link
          to={`/${npub}`}
          onClick={e => e.stopPropagation()}
          className="flex-shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={`View ${displayName}'s profile flag`}
        >
          <Avatar className="h-7 w-7 hover:opacity-80 transition-opacity">
            <AvatarImage src={avatar} />
            <AvatarFallback className="text-xs">{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onClick(event)}>
          <p className="font-semibold text-sm truncate text-foreground">{title}</p>
          <Link
            to={`/${npub}`}
            onClick={e => e.stopPropagation()}
            className="text-xs text-muted-foreground truncate hover:text-foreground transition-colors block"
          >
            {displayName}
          </Link>
        </div>
        <Badge variant="outline" className="text-xs flex-shrink-0 cursor-pointer" onClick={() => onClick(event)}>
          {encoding}
        </Badge>
      </div>
    </div>
  );
}
